import { formatJobNumber } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { addressLine, formatFeedTime, formatTime, money } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { stageLabel, stageOf } from '../../lib/job-stages.js'
import { useIsTablet } from '../../lib/media.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { EmailCompose } from '../email/EmailCompose.js'
import { FileViewer } from '../FileViewer.js'
import { Icon, type IconName } from '../Icon.js'
import { Modal } from '../Modal.js'
import { useOverlay } from '../overlay.js'
import { ServicesPane, type ReportPdf } from '../job-card/ServicesPane.js'
import { SmsCompose } from '../job-card/SmsCompose.js'
import { attachmentFile, buildFeed } from '../job-card/feed.js'
import { emailContacts, nextVisit, primaryPerson } from '../job-card/panes.js'
import { reportEmailDraft, serviceStatus } from '../job-card/services.js'
import type { Job, JobVisit } from '../job-card/types.js'
import { Badge, ErrorState, SkeletonRows, type Tone } from '../ui.js'
import { FieldBilling } from './FieldBilling.js'
import { CaptureBar, NoteSheet, SectionLink, captureBarRoom, useCapture } from './FieldCapture.js'
import { FieldFeed, type FeedFilter } from './FieldFeed.js'
import { FieldPeople } from './FieldPeople.js'
import {
  GroupLabel,
  KnockNotes,
  NextStepButton,
  VisitBadge,
  cardClass,
  directionsHref,
  hasKnockNotes,
  lengthOf,
  telHref,
  timeRange,
  untilLabel,
  workOf,
} from './kit.js'

/** What is open over the job: its feed, its bill, or a note being written. */
export type FieldPanel = { kind: 'feed'; filter: FeedFilter } | { kind: 'billing' } | { kind: 'note' } | null

const EXIT_MS = 260

/**
 * Where the field app draws the open job.
 *
 * A phone pushes it on as a page from the right, with a back button naming
 * the screen underneath; an iPad puts it in a dialog over a screen that has
 * no detail pane, and draws nothing here when the shell has already put it
 * in the right-hand pane (`inline`).
 */
export function FieldJobLayer({ inline }: { inline: boolean }) {
  const { jobId, closeJob } = useJobOverlay()
  const tablet = useIsTablet()

  if (jobId === null || inline) return null

  if (tablet) {
    return (
      <Modal
        open
        onClose={closeJob}
        labelledBy="field-job-title"
        className="h-[min(880px,calc(100vh-2rem))] max-w-[680px] rounded-[18px] bg-ink-100"
      >
        <FieldJob key={jobId} jobId={jobId} onClose={closeJob} />
      </Modal>
    )
  }

  return <FieldJobPage key={jobId} jobId={jobId} onClose={closeJob} />
}

/**
 * The job as a pushed page. It joins the overlay stack like a `Sheet`, so
 * Escape closes whatever is on top and the feed and the bill stack over it.
 */
function FieldJobPage({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { pathname } = useLocation()
  const [entered, setEntered] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const exit = useRef<number | null>(null)

  const close = useCallback(() => {
    if (exit.current !== null) return
    setLeaving(true)
    exit.current = window.setTimeout(onClose, EXIT_MS)
  }, [onClose])

  useEffect(
    () => () => {
      if (exit.current !== null) clearTimeout(exit.current)
    },
    [],
  )

  const depth = useOverlay(true, close)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="field-job-title"
      style={{ zIndex: 50 + depth * 10 }}
      className={cn(
        'fixed inset-0 flex flex-col bg-ink-100 pt-[var(--safe-top)] shadow-[-8px_0_30px_rgb(4_8_6_/_0.1)] transition-transform duration-[360ms] ease-[cubic-bezier(.32,.72,0,1)]',
        entered && !leaving ? 'translate-x-0' : 'translate-x-[102%]',
      )}
    >
      <FieldJob jobId={jobId} back={{ label: backLabel(pathname), onBack: close }} />
    </div>,
    document.body,
  )
}

function backLabel(pathname: string): string {
  if (pathname === '/') return 'Today'
  if (pathname.startsWith('/jobs')) return 'Jobs'
  if (pathname.startsWith('/team')) return 'Team'
  if (pathname.startsWith('/inbox')) return 'Inbox'
  if (pathname.startsWith('/me')) return 'Me'
  return 'Back'
}

/**
 * A job, the way a technician holds it.
 *
 * Top to bottom in the order they need it at the door: who and where, the
 * booking as a dark card with the one next step and the call and the
 * directions, what the site will do to you, the work and its reports, the
 * bill, the office's brief, what has been captured, and the people. Camera,
 * Note, Voice and Billing float over the foot of the screen.
 *
 * The feed and the bill open over the job rather than replacing it, so a voice
 * clip keeps recording and Done comes back to the same place. There is no
 * way through to the office's tabbed card from here on purpose: what the
 * phone needs is built into it.
 */
export function FieldJob({
  jobId,
  back,
  onClose,
}: {
  jobId: string
  /** On a phone the header leads with a back button to the screen underneath. */
  back?: { label: string; onBack: () => void }
  /** In a dialog the header ends with Done. */
  onClose?: () => void
}) {
  const trpc = useTRPC()
  const [panel, setPanel] = useState<FieldPanel>(null)

  const { data: job, isPending, error, refetch } = useQuery(trpc.jobs.byId.queryOptions({ id: jobId }))

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows rows={5} />

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-ink-100">
      <TopBar
        job={job}
        back={back}
        onClose={onClose}
        onFeed={() => setPanel({ kind: 'feed', filter: 'all' })}
      />
      <JobBody job={job} panel={panel} onPanel={setPanel} />
    </div>
  )
}

function TopBar({
  job,
  back,
  onClose,
  onFeed,
}: {
  job: Job
  back?: { label: string; onBack: () => void }
  onClose?: () => void
  onFeed: () => void
}) {
  const { user } = useSession()
  const visit = focusVisit(job, user?.id)
  const stage = stageOf(job.status)

  return (
    <div
      className={cn(
        'flex h-12 shrink-0 items-center gap-2.5 bg-white/85 pr-4 shadow-[inset_0_-1px_0_var(--color-ink-200)] backdrop-blur-xl',
        back ? 'pl-2' : 'pl-5',
      )}
    >
      {back ? (
        <button
          type="button"
          onClick={back.onBack}
          className="flex shrink-0 items-center py-1.5 pr-1 text-[17px] text-brand-700"
        >
          <Icon name="back" className="size-6" />
          {back.label}
        </button>
      ) : null}
      <span className="shrink-0 font-mono text-[13px] font-medium text-ink-500">
        {formatJobNumber(job)}
      </span>
      {visit ? (
        <VisitBadge status={visit.status} />
      ) : stage ? (
        <Badge className="rounded-md px-2 py-0.5 text-xs">{stageLabel(stage)}</Badge>
      ) : null}
      <button
        type="button"
        onClick={onFeed}
        className="ml-auto flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-brand-800 pr-3 pl-2.5 text-[14px] font-semibold text-white active:bg-brand-900"
      >
        <Icon name="activity" className="size-4 text-brand-200" strokeWidth={2} />
        Feed
      </button>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 py-2 pl-1 text-[17px] font-semibold text-brand-700"
        >
          Done
        </button>
      ) : null}
    </div>
  )
}

/**
 * The booking the page is about: the one I am standing at, else the one I am
 * on today, else anybody's live one, else the next.
 */
function focusVisit(job: Job, userId: string | undefined): JobVisit | undefined {
  const live = job.visits.filter(
    (visit) => visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW',
  )
  const mine = live.filter((visit) =>
    visit.assignments.some((assignment) => assignment.userId === userId),
  )
  const today = new Date().toDateString()
  const active = (list: JobVisit[]) =>
    list.find((visit) => visit.status === 'ON_SITE' || visit.status === 'EN_ROUTE')
  const booked = (list: JobVisit[]) =>
    list.find(
      (visit) =>
        new Date(visit.startAt).toDateString() === today && visit.status !== 'COMPLETED',
    )
  return active(mine) ?? booked(mine) ?? active(live) ?? booked(live) ?? nextVisit(job)
}

function dayLabel(value: Date | string): string {
  const date = new Date(value)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
}

function JobBody({
  job,
  panel,
  onPanel,
}: {
  job: Job
  panel: FieldPanel
  onPanel: (panel: FieldPanel) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { user, can } = useSession()
  const { openReport } = useJobOverlay()
  const capture = useCapture(job.id)
  const [emailTo, setEmailTo] = useState<string | null>(null)
  const [reportEmail, setReportEmail] = useState<ReportPdf | null>(null)
  const [smsTo, setSmsTo] = useState<{ phone: string; name: string } | null>(null)

  const canEdit = can('JOB_EDIT')
  const visit = focusVisit(job, user?.id)
  const person = primaryPerson(job)
  const phone = person?.phone ?? job.client.phone ?? null
  const line = job.site ? addressLine(job.site.address) : null
  const feed = buildFeed(job)
  const subtitle = `${job.client.name} · ${formatJobNumber(job)}`

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
  }
  const addNote = useMutation(trpc.jobs.addNote.mutationOptions({ onSuccess: invalidate }))
  const removeService = useMutation(
    trpc.jobs.removeService.mutationOptions({ onSuccess: invalidate }),
  )

  // The report the check-out refusal is about: the first one not finished.
  const unfinished = (job.services ?? []).find(
    (service) => service.serviceType.formId !== null && !serviceStatus(service).done,
  )
  const mayCheckIn =
    visit !== undefined &&
    (can('SCHEDULE_EDIT') ||
      visit.assignments.some((assignment) => assignment.userId === user?.id))

  const brief = job.description?.trim() || job.scope?.trim() || null
  const openFeed = (filter: FeedFilter) => onPanel({ kind: 'feed', filter })
  const openBilling = can('INVOICE_VIEW') ? () => onPanel({ kind: 'billing' }) : undefined
  const openNote = () => onPanel({ kind: 'note' })

  return (
    <>
      {capture.inputs}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div
          className={cn(
            'mx-auto flex w-full max-w-[640px] flex-col gap-4 px-4 pt-[18px]',
            captureBarRoom,
          )}
        >
          <div className="px-1">
            <h2
              id="field-job-title"
              className="text-[28px] leading-[1.15] font-bold tracking-[-0.02em] text-pretty text-ink-900"
            >
              {job.client.name}
            </h2>
            {line ? <p className="mt-1 text-base text-ink-600">{line}</p> : null}
            <p className="mt-1 text-sm text-ink-500">
              {[
                workOf(job),
                visit
                  ? `${dayLabel(visit.startAt)} · ${timeRange(visit.startAt, visit.endAt)} · ${lengthOf(visit.startAt, visit.endAt)}`
                  : 'Not booked yet',
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          <LiveCard
            visit={visit}
            mayAct={mayCheckIn}
            phone={phone}
            directions={directionsHref(job.site?.address, line)}
            onOpenReport={unfinished ? () => openReport(unfinished.id) : undefined}
          />

          {hasKnockNotes(job.site) ? (
            <section className={cn(cardClass, 'flex flex-col gap-2.5 p-3.5')}>
              <h3 className="text-[12px] font-semibold tracking-[0.05em] text-ink-500 uppercase">
                Before you knock
              </h3>
              <KnockNotes site={job.site} showAccess />
            </section>
          ) : null}

          <ServicesPane
            field
            job={job}
            canEdit={canEdit}
            isAddingNote={addNote.isPending}
            onAddNote={(jobServiceId, body, done) =>
              addNote.mutate({ body, jobId: job.id, jobServiceId }, { onSuccess: done })
            }
            onRemoveService={(id) => removeService.mutate({ id })}
            onEmailReport={setReportEmail}
          />

          {openBilling ? <BillCard job={job} onOpen={openBilling} /> : null}

          <section>
            <GroupLabel>From the office</GroupLabel>
            <div className={cn(cardClass, 'p-3.5')}>
              <p
                className={cn(
                  'text-[15px] leading-relaxed text-pretty whitespace-pre-line',
                  brief ? 'text-ink-800' : 'text-ink-500',
                )}
              >
                {brief ?? 'No notes from the office.'}
              </p>
            </div>
          </section>

          <Media job={job} onSeeAll={() => openFeed('media')} />

          <Notes job={job} onSeeAll={() => openFeed('notes')} />

          <button
            type="button"
            onClick={() => openFeed('all')}
            className={cn(cardClass, 'flex min-h-[54px] w-full items-center gap-3 px-3.5 text-left active:bg-ink-50')}
          >
            <span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-brand-800 text-brand-200">
              <Icon name="activity" className="size-4" strokeWidth={1.9} />
            </span>
            <span className="min-w-0 flex-1 text-base font-semibold text-ink-900">View full feed</span>
            <span className="shrink-0 text-[14px] text-ink-500 tabular-nums">{feed.length}</span>
            <Icon name="forward" className="size-[18px] shrink-0 text-ink-400" strokeWidth={2} />
          </button>

          <FieldPeople
            job={job}
            canEdit={canEdit}
            onText={(number, name) => setSmsTo({ phone: number, name })}
            onEmail={(address) => setEmailTo(address)}
          />
        </div>
      </div>

      <CaptureBar capture={capture} canCapture={canEdit} onNote={openNote} onBilling={openBilling} />

      {panel?.kind === 'feed' ? (
        <FieldFeed
          job={job}
          filter={panel.filter}
          onFilter={(filter) => onPanel({ kind: 'feed', filter })}
          onClose={() => onPanel(null)}
          capture={capture}
          canCapture={canEdit}
          onNote={openNote}
          onBilling={openBilling}
        />
      ) : null}
      {panel?.kind === 'billing' ? <FieldBilling job={job} onClose={() => onPanel(null)} /> : null}
      {panel?.kind === 'note' ? (
        <NoteSheet jobId={job.id} subtitle={subtitle} onClose={() => onPanel(null)} />
      ) : null}

      <EmailCompose
        open={emailTo !== null}
        onClose={() => setEmailTo(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={job.attachments.map((file) => ({ id: file.id, name: file.filename }))}
        initial={{ to: emailTo ? [emailTo] : [], subject: `Job ${formatJobNumber(job)}` }}
      />
      <EmailCompose
        open={reportEmail !== null}
        onClose={() => setReportEmail(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={[
          ...job.attachments.map((file) => ({ id: file.id, name: file.filename })),
          ...(reportEmail && !job.attachments.some((file) => file.id === reportEmail.attachmentId)
            ? [{ id: reportEmail.attachmentId, name: reportEmail.filename }]
            : []),
        ]}
        initial={
          reportEmail
            ? reportEmailDraft(job, reportEmail, {
                name: person?.name ?? null,
                email: person?.email ?? job.client.email ?? null,
              })
            : {}
        }
      />
      {smsTo ? (
        <SmsCompose open onClose={() => setSmsTo(null)} phone={smsTo.phone} contactName={smsTo.name} jobId={job.id} />
      ) : null}
    </>
  )
}

interface Live {
  label: string
  sub: string
  big: string
  /** The dot, its halo and the label share one colour. */
  colour: 'blue' | 'amber' | 'green' | 'quiet'
}

const LIVE_COLOUR: Record<Live['colour'], { dot: string; text: string }> = {
  blue: { dot: 'bg-blue-400 ring-blue-400/20', text: 'text-blue-400' },
  amber: { dot: 'bg-amber-400 ring-amber-400/20', text: 'text-amber-400' },
  green: { dot: 'bg-green-400 ring-green-400/25', text: 'text-green-400' },
  quiet: { dot: 'bg-ink-400 ring-ink-400/20', text: 'text-ink-400' },
}

/** What the live card says about a booking at a given moment. */
function liveOf(visit: JobVisit | undefined, now: number): Live {
  if (!visit) return { label: 'Not booked', sub: '', big: 'No booking yet', colour: 'quiet' }

  const start = new Date(visit.startAt)
  if (visit.status === 'ON_SITE') {
    const since = new Date(visit.arrivedAt ?? visit.startAt)
    return {
      label: 'On site',
      sub: `since ${formatTime(since)}`,
      big: clock(now - since.getTime()),
      colour: 'green',
    }
  }
  if (visit.status === 'EN_ROUTE') {
    return { label: 'On your way', sub: untilLabel(start), big: `Due ${formatTime(start)}`, colour: 'amber' }
  }
  if (visit.status === 'COMPLETED') {
    return {
      label: 'Completed',
      sub: dayLabel(start),
      big:
        visit.arrivedAt && visit.departedAt
          ? lengthOf(visit.arrivedAt, visit.departedAt)
          : lengthOf(visit.startAt, visit.endAt),
      colour: 'green',
    }
  }
  const today = start.toDateString() === new Date(now).toDateString()
  return {
    label: today ? 'Booked today' : 'Booked',
    sub: today ? (start.getTime() > now ? untilLabel(start) : 'now') : dayLabel(start),
    big: formatTime(start),
    colour: 'blue',
  }
}

/** "0:42:07", for the time on site. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor(total / 60) % 60
  const seconds = total % 60
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(timer)
  }, [everyMs])
  return now
}

/**
 * The booking as the page's one dark card: where it stands, the time that
 * matters, the next step, and the call and the directions. It is what the
 * old dock held, moved up to where the eye lands.
 */
function LiveCard({
  visit,
  mayAct,
  phone,
  directions,
  onOpenReport,
}: {
  visit: JobVisit | undefined
  mayAct: boolean
  phone: string | null
  directions: string | null
  onOpenReport?: () => void
}) {
  const now = useNow(visit?.status === 'ON_SITE' ? 1000 : 30_000)
  const live = liveOf(visit, now)
  const colour = LIVE_COLOUR[live.colour]

  return (
    <section
      aria-label="Booking"
      className="overflow-hidden rounded-[22px] bg-brand-800 shadow-[0_10px_26px_rgb(20_35_25_/_0.2)]"
    >
      <div className="flex items-center gap-2 px-[18px] pt-4">
        <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full ring-4', colour.dot)} />
        <span className={cn('text-[12px] font-bold tracking-[0.1em] uppercase', colour.text)}>
          {live.label}
        </span>
        <span className="ml-auto shrink-0 text-[13px] text-sky/70 tabular-nums">{live.sub}</span>
      </div>
      <p
        className={cn(
          'px-[18px] pt-2 leading-[1.1] font-semibold tracking-[-0.03em] text-white tabular-nums',
          visit ? 'text-[42px]' : 'text-[30px]',
        )}
      >
        {live.big}
      </p>
      <div className="flex flex-col gap-2.5 px-[18px] pt-4 pb-[18px]">
        {visit ? (
          mayAct ? (
            <NextStepButton
              tone="dark"
              visitId={visit.id}
              status={visit.status}
              onOpenReport={onOpenReport}
            />
          ) : (
            <p className="flex h-[54px] items-center justify-center rounded-[15px] bg-white/10 text-[15px] font-semibold text-ink-300">
              Booked for someone else
            </p>
          )
        ) : null}
        <div className="grid grid-cols-2 gap-2.5">
          <LiveLink icon="phone" label="Call" href={telHref(phone)} />
          <LiveLink icon="map" label="Directions" href={directions} />
        </div>
      </div>
    </section>
  )
}

function LiveLink({ icon, label, href }: { icon: IconName; label: string; href: string | null }) {
  const shape =
    'flex h-[46px] items-center justify-center gap-2 rounded-[14px] bg-white/10 text-[15px] font-semibold text-white'
  if (!href) {
    return (
      <span aria-disabled="true" className={cn(shape, 'opacity-40')}>
        <Icon name={icon} className="size-[18px]" />
        {label}
      </span>
    )
  }
  return (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noreferrer' : undefined}
      className={cn(shape, 'active:bg-white/15')}
    >
      <Icon name={icon} className="size-[18px]" />
      {label}
    </a>
  )
}

/**
 * The bill as it stands, for whoever may see invoices. The figure is the
 * job's live invoice from `jobs.byId`, so it costs no request of its own;
 * the lines and the payments are the billing sheet's to fetch.
 */
function BillCard({ job, onOpen }: { job: Job; onOpen: () => void }) {
  const { can } = useSession()
  const invoice = job.invoices.find((row) => row.status !== 'VOID')
  const quote = job.quotes.find((row) => row.status !== 'SUPERSEDED')
  const due = invoice ? Math.max(0, invoice.totalIncTax - invoice.amountPaid) : 0

  const [label, tone]: [string, Tone] = !invoice
    ? ['No items', 'neutral']
    : due === 0 && invoice.totalIncTax > 0
      ? ['Paid', 'green']
      : invoice.amountPaid > 0
        ? ['Part paid', 'amber']
        : invoice.status === 'DRAFT'
          ? ['Draft', 'neutral']
          : ['Unpaid', 'amber']

  const action =
    invoice && due > 0 && can('PAYMENT_RECORD')
      ? 'Take payment'
      : !invoice && can('INVOICE_EDIT')
        ? 'Add items'
        : 'View'

  return (
    <section>
      <GroupLabel>Billing</GroupLabel>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Billing, ${invoice ? money(invoice.totalIncTax) : 'nothing billed yet'}`}
        className={cn(cardClass, 'flex w-full items-center gap-3 px-3.5 py-3 text-left active:bg-ink-50')}
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-800 text-brand-200">
          <Icon name="pricing" className="size-[22px]" strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[17px] font-semibold text-ink-900 tabular-nums">
              {money(invoice?.totalIncTax ?? 0)}
            </span>
            <Badge tone={tone} className="rounded-md px-2 py-0.5 text-xs">
              {label}
            </Badge>
          </span>
          <span className="mt-0.5 block text-[13px] leading-snug text-pretty text-ink-500">
            {invoice
              ? due > 0
                ? `Balance due ${money(due)}`
                : 'Nothing owing'
              : quote
                ? `Quote ${quote.number} for ${money(quote.totalIncTax)} · no invoice yet`
                : 'Nothing billed yet'}
          </span>
        </span>
        <span
          className={cn(
            'inline-flex h-8 shrink-0 items-center rounded-full px-3 text-[13px] font-semibold',
            action === 'View' ? 'bg-ink-100 text-ink-700' : 'bg-brand-600 text-white',
          )}
        >
          {action}
        </span>
      </button>
    </section>
  )
}

/**
 * The job's photographs and videos, four across. Adding one is Camera on
 * the bar; See all is the feed's Photos & video.
 */
function Media({ job, onSeeAll }: { job: Job; onSeeAll: () => void }) {
  const [openId, setOpenId] = useState<string | null>(null)

  const files = job.attachments
    .map((attachment) => ({ attachment, file: attachmentFile(attachment) }))
    .filter(
      ({ attachment, file }) =>
        file.url && (file.isImage || (attachment.mimeType ?? '').startsWith('video/')),
    )
  const photos = files.filter(({ file }) => file.isImage).map(({ file }) => file)
  const shown = files.slice(0, 8)

  if (files.length === 0) return null

  return (
    <section>
      <GroupLabel aside={<SectionLink onClick={onSeeAll}>See all</SectionLink>}>
        Photos &amp; video · {files.length}
      </GroupLabel>
      <div className="grid grid-cols-4 gap-2">
        {shown.map(({ file }, index) => {
          const more = index === shown.length - 1 && files.length > shown.length
          return (
            <button
              key={file.id}
              type="button"
              onClick={() => (file.isImage && !more ? setOpenId(file.id) : onSeeAll())}
              aria-label={more ? `See all ${files.length}` : `Open ${file.name}`}
              className="relative aspect-square overflow-hidden rounded-xl bg-ink-200 ring-1 ring-ink-300 ring-inset"
            >
              {file.isImage ? (
                <img src={file.url ?? undefined} alt="" className="size-full object-cover" />
              ) : (
                <span className="absolute top-1/2 left-1/2 grid size-[30px] -translate-1/2 place-items-center rounded-full bg-ink-900/70 text-white">
                  <Icon name="video" className="size-3.5" />
                </span>
              )}
              {more ? (
                <span className="absolute inset-0 grid place-items-center bg-ink-900/55 text-base font-semibold text-white">
                  +{files.length - shown.length + 1}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
      <FileViewer files={photos} openId={openId} onClose={() => setOpenId(null)} />
    </section>
  )
}

/** The latest notes on the job, whoever wrote them. See all is the feed's Notes. */
function Notes({ job, onSeeAll }: { job: Job; onSeeAll: () => void }) {
  const notes = buildFeed(job)
    .filter((entry) => entry.kind === 'note')
    .sort((a, b) => b.at.getTime() - a.at.getTime())

  if (notes.length === 0) return null

  return (
    <section>
      <GroupLabel aside={<SectionLink onClick={onSeeAll}>See all</SectionLink>}>
        Notes · {notes.length}
      </GroupLabel>
      <div className={cn(cardClass, 'divide-y divide-ink-100')}>
        {notes.slice(0, 3).map((note) => (
          <div key={note.id} className="flex gap-3 px-3.5 py-3">
            <span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-brand-50 text-brand-700">
              <Icon name="note" className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] text-ink-500">
                {[note.who, noteTime(note.at)].filter(Boolean).join(' · ')}
              </p>
              <p className="mt-0.5 line-clamp-3 text-[15px] leading-snug text-pretty whitespace-pre-line text-ink-900">
                {note.body ?? note.text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function noteTime(at: Date): string {
  return at.toDateString() === new Date().toDateString() ? formatTime(at) : formatFeedTime(at)
}
